import { isDefined } from "@/utils/utils";
import { t } from "i18next";
import AgentActiveTag from "../../../../modules/agent/components/tags/AgentActiveTag";
import AgentCanBeGainerTag from "../../../../modules/agent/components/tags/AgentCanBeGainerTag";
import { AgentTypeLongNameTag } from "../../../../modules/agent/components/tags/AgenTypeLongNameTag";
import { AgentSecondaryStatus, AgentStatus, AgentType, FinancialSector } from "../../../../modules/agent/enums";
import {
  AgentBase,
  AgentDirectorateBase,
  LegalAgent,
  NaturalAgent,
  SelfEmployedAgent,
} from "../../../../modules/agent/types";
import { convertDtypeToAgentType, formatAgentDirectorateName } from "../../../../modules/agent/utils";
import { AgentEnumName } from "../../../../modules/enumerations/components/view/AgentEnumName";
import { ACCESS_TREE_PATH_DELIMITER } from "../../../constants";
import {
  AgentHistoryItem,
  BooleanHistoryItem,
  DateHistoryItem,
  EducationLevelHistoryItem,
  EmptyHistoryItem,
  FinancialSectorsHistoryItem,
  IbanHistoryItem,
  NoteHistoryItem,
  PhoneHistoryItem,
} from "./commonHistoryItems";

const AccessTreePathItem = (value: string) => {
  const tokens = (value as string).split(ACCESS_TREE_PATH_DELIMITER);
  return <AgentEnumName id={tokens[tokens.length - 2] as string} />;
};

const CanBeGainerItem = (value: boolean) => <AgentCanBeGainerTag canBeGainer={value} />;

const DeactivatedItem = (value: boolean) => <AgentActiveTag agentDeactivated={value} />;

const SystemIdNumberItem = (value: number) => <span>{value.toString().padStart(5, "0")}</span>;

const StructureIdNumberItem = (value: number) =>
  isDefined(value) ? <span>{value.toString().padStart(3, "0")}</span> : <EmptyHistoryItem />;

const TypeItem = (value?: AgentType) => (value ? <AgentTypeLongNameTag type={value} /> : <EmptyHistoryItem />);

const AgentDirectorateItem = (value: AgentDirectorateBase) =>
  value ? <span>{formatAgentDirectorateName(value)}</span> : <EmptyHistoryItem />;

const AgentStatusItem = (value: AgentStatus) =>
  value ? <span>{t("agent.enums.agentStatus." + value)}</span> : <EmptyHistoryItem />;

const AgentSecondaryStatusItem = (value: AgentSecondaryStatus) =>
  value ? <span>{t("agent.enums.agentSecondaryStatus." + value)}</span> : <EmptyHistoryItem />;

export const AgentHistoryItemsMap = new Map<
  Partial<keyof NaturalAgent> | Partial<keyof SelfEmployedAgent> | Partial<keyof LegalAgent>,
  (
    value:
      | string
      | number
      | boolean
      | AgentBase
      | FinancialSector[]
      | AgentType
      | AgentDirectorateBase
      | AgentStatus
      | AgentSecondaryStatus,
  ) => React.JSX.Element
>([
  ["accessTreePath", (value) => AccessTreePathItem(value as string)],
  ["bankAccountNumber", (value) => IbanHistoryItem(value as string)],
  ["birthDate", (value) => DateHistoryItem(value as string)],
  ["canBeGainer", (value) => CanBeGainerItem(value as boolean)],
  ["deactivated", (value) => DeactivatedItem(value as boolean)],
  ["systemIdNumber", (value) => SystemIdNumberItem(value as number)],
  ["note", (value) => NoteHistoryItem(value as string)],
  ["parent", (value) => AgentHistoryItem(value as AgentBase)],
  ["phone", (value) => PhoneHistoryItem(value as string)],
  ["sectors", (value) => FinancialSectorsHistoryItem(value as FinancialSector[])],
  ["structureIdNumber", (value) => StructureIdNumberItem(value as number)],
  ["type", (value) => TypeItem(value as AgentType)],
  ["dtype", (value) => TypeItem(convertDtypeToAgentType(value as string))],
  ["directorate", (value) => AgentDirectorateItem(value as AgentDirectorateBase)],
  ["status", (value) => AgentStatusItem(value as AgentStatus)],
  ["statusStartDate", (value) => DateHistoryItem(value as string)],
  ["statusEndDate", (value) => DateHistoryItem(value as string)],
  ["secondaryStatus", (value) => AgentSecondaryStatusItem(value as AgentSecondaryStatus)],
  ["secondaryStatusStartDate", (value) => DateHistoryItem(value as string)],
  ["secondaryStatusEndDate", (value) => DateHistoryItem(value as string)],
  ["realtySpecialist", (value) => BooleanHistoryItem(value as boolean)],
  ["loanSpecialist", (value) => BooleanHistoryItem(value as boolean)],
  ["educationLevel", (value) => EducationLevelHistoryItem(value as string)],
  ["legalTypeChangeDate", (value) => DateHistoryItem(value as string)],
]);
